import { useEffect, useState } from 'react'
import { fetchStudentInsights } from 'api/reports'
import {
	PERIOD_ACADEMIC_YEAR,
	buildInsightsDateParams,
	isSelectableReportPeriod,
} from '../utils/reportsPeriods'

const emptyInsights = () => ({
	period: null,
	filter: { grade: 'all' },
	grades: [],
	roster: { total: 0, activeInPeriod: 0, active30: 0, active90: 0 },
	doneInPeriod: {
		withTasksCompleted: 0,
		withExplorationSaved: 0,
		withCareerProfile: 0,
		withAssessment: 0,
	},
	doneSnapshot: {
		withTasksAssigned: 0,
		withExplorationSaved: 0,
		withCareerProfile: 0,
		withAssessment: 0,
	},
	interestFamilies: [],
	topCareers: [],
	topUniversities: [],
	topCountries: [],
	personality: [],
	appPrep: {
		applications: 0,
		studentsWithApps: 0,
		essaysAssigned: 0,
		essaysApproved: 0,
		lorsCompleted: 0,
		lorsNeeded: 0,
		docsCompleted: 0,
		docsTotal: 0,
	},
	coverage: { withSessionInPeriod: 0, withApplication: 0, withAssignment: 0 },
	taskCompletionPct: 0,
	avgProjectsShortlisted: 0,
	priorityStudents: [],
	complianceLive: {
		assessed: 0,
		withDestinationPlan: 0,
		withFinalOutcome: 0,
		eligibleTotal: 0,
	},
	students: [],
})

/**
 * Loads Student Status report data from the APPLIED reporting period only.
 * status: 'idle' | 'loading' | 'success' | 'error'
 */
export const useStudentInsights = ({ period, grade, customFrom, customTo }) => {
	const [state, setState] = useState({
		status: 'loading',
		data: emptyInsights(),
		error: null,
	})

	useEffect(() => {
		let active = true
		const periodKey = isSelectableReportPeriod(period) ? period : PERIOD_ACADEMIC_YEAR
		const gradeKey = grade || 'all'
		const dateParams = buildInsightsDateParams({
			period: periodKey,
			customFrom,
			customTo,
		})

		if (!dateParams) {
			setState(prev => ({
				...prev,
				status: 'idle',
				error: null,
			}))
			return () => {
				active = false
			}
		}

		setState(prev => ({
			...prev,
			status: 'loading',
			error: null,
		}))

		const load = async () => {
			try {
				const data = await fetchStudentInsights({
					...dateParams,
					grade: gradeKey,
				})
				if (!active) return
				setState({
					status: 'success',
					data: data || emptyInsights(),
					error: null,
				})
			} catch (error) {
				if (!active) return
				setState({
					status: 'error',
					data: emptyInsights(),
					error:
						error?.response?.data?.error ||
						error?.message ||
						'Failed to load student insights',
				})
			}
		}

		load()
		return () => {
			active = false
		}
	}, [period, grade, customFrom, customTo])

	return state
}
